import { Injectable } from '@angular/core';
import { FilterSettingsModel, GridComponent, PredicateModel } from '@syncfusion/ej2-angular-grids';

export const AUDIT_TAB_KEY = 'audit';
export const APPROVAL_PAYMENT_TAB_KEY = 'approval-payment';
export const APPROVAL_EXPECT_FUND_TAB_KEY = 'approval-expect-fund';
export const APPROVAL_TRANSFER_TAB_KEY = 'approval-transfer';
export const APPROVAL_JOURNAL_TAB_KEY = 'approval-journal';

/** Builds the key for a My Requests / Completed grid, which share the same history components. */
export const historyTabKey = (isCompletedTab: boolean, requestType: 'payment' | 'expect-fund' | 'transfer' | 'journal'): string =>
    `${isCompletedTab ? 'completed' : 'my-requests'}-${requestType}`;

/** Builds the key for a Drafts grid (one per request type). */
export const draftsTabKey = (requestType: string): string => `drafts-${requestType}`;

/**
 * Keeps the grid filters applied on each tab, in memory only, so they survive navigation between
 * tabs but are discarded on a browser refresh or logout/login (both reload the page).
 */
@Injectable({
    providedIn: 'root'
})
export class TabFilterStateService {
    private readonly filters = new Map<string, PredicateModel[]>();

    /** Grid filter settings that start with the filters remembered for the tab. */
    getFilterSettings(tabKey: string): FilterSettingsModel {
        return { type: 'Menu', columns: this.get(tabKey) };
    }

    /** Remembers the filters currently applied on the grid. */
    save(tabKey: string, grid: GridComponent | undefined): void {
        this.set(tabKey, grid?.filterSettings.columns);
    }

    /** Clears the filters on the grid and forgets them. */
    clearFiltering(tabKey: string, grid: GridComponent | undefined): void {
        this.syncColumnUids(grid);
        grid?.clearFiltering();
        this.clear(tabKey);
    }

    get(tabKey: string): PredicateModel[] {
        return this.copy(this.filters.get(tabKey) ?? []);
    }

    set(tabKey: string, columns: PredicateModel[] | undefined): void {
        if (!columns || columns.length === 0) {
            this.filters.delete(tabKey);
            return;
        }
        this.filters.set(tabKey, this.copy(columns));
    }

    clear(tabKey: string): void {
        this.filters.delete(tabKey);
    }

    // Filters re-applied on load can hold column uids from before the grid rebuilt its columns,
    // which makes clearFiltering() throw. Point every filter at its current column first.
    private syncColumnUids(grid: GridComponent | undefined): void {
        (grid?.filterSettings.columns ?? []).forEach(filter => {
            const column = grid?.getColumnByField(filter.field ?? '');
            if (column) filter.uid = column.uid;
        });
    }

    private copy(columns: PredicateModel[]): PredicateModel[] {
        // uid identifies a column of one specific grid instance; a new grid assigns its own, so a stale one breaks clearFiltering
        return columns.map(({ uid, ...column }) => ({ ...column }));
    }
}
